import React, { useCallback, useEffect, useState } from 'react'
import {
  coachingService,
  type CoachContext,
  type DailyBrief,
  type LearningPlan,
  type WeeklyReview,
  type WorkforceRecommendation,
  type CoachMessage,
} from '../../../services/coaching'

// AI Coach panel — orchestrates the existing lesson/competency/certification engines
// into a mentor-like experience (daily brief, plan, goals, ask, weekly review,
// workforce-aware recommendations). The backend never exposes its internal persona.
export const AiCoachPanel: React.FC<{refreshRevision?:number}> = ({refreshRevision=0}) => {
  const [context, setContext] = useState<CoachContext | null>(null)
  const [brief, setBrief] = useState<DailyBrief | null>(null)
  const [plan, setPlan] = useState<LearningPlan[]>([])
  const [review, setReview] = useState<WeeklyReview | null>(null)
  const [workforce, setWorkforce] = useState<WorkforceRecommendation[]>([])
  const [messages, setMessages] = useState<CoachMessage[]>([])
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [goalTitle, setGoalTitle] = useState('')
  const [conversationId, setConversationId] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [ctx, dailyBrief, planResp, weekly, work] = await Promise.all([
        coachingService.getContext(),
        coachingService.getBrief(),
        coachingService.getPlan(),
        coachingService.getWeeklyReview(),
        coachingService.getWorkforceRecommendations(),
      ])
      setContext(ctx.data.context)
      setBrief(dailyBrief.data.brief)
      setPlan(planResp.data.plan.plans)
      setReview(weekly.data.review)
      setWorkforce(work.data.recommendations)
    } catch {
      setError('Unable to load your coach right now.')
    }
  }, [])

  useEffect(() => { void load() }, [load,refreshRevision])

  const ask = useCallback(async () => {
    const message = draft.trim()
    if (!message) return
    setBusy(true)
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: 'learner', content: message, intent: null, createdAt: new Date().toISOString() }])
    setDraft('')
    try {
      const result = await coachingService.ask(message, conversationId)
      setConversationId(result.data.conversationId)
      setMessages((prev) => [...prev, { id: `coach-${Date.now()}`, role: 'coach', content: result.data.reply, intent: result.data.intent, createdAt: new Date().toISOString() }])
      void load()
    } catch {
      setError('The coach could not reply just now.')
    } finally {
      setBusy(false)
    }
  }, [draft, conversationId, load])

  const addGoal = useCallback(async () => {
    const title = goalTitle.trim()
    if (!title) return
    setBusy(true)
    try {
      await coachingService.createGoal({ title })
      setGoalTitle('')
      void load()
    } catch {
      setError('Could not save that goal.')
    } finally {
      setBusy(false)
    }
  }, [goalTitle, load])

  if (error && !context) {
    return <section className="panel workspace-panel stack" data-testid="ai-coach-error"><p className="text-muted">{error}</p></section>
  }

  return (
    <section className="panel workspace-panel stack-lg" data-testid="ai-coach-panel">
      <header className="stack-sm">
        <h2 className="heading-lg">Your AI Coach</h2>
        {context && <p className="text-muted">{context.momentum} · {context.streak.currentStreakDays}-day streak</p>}
      </header>

      {brief && (
        <div className="card stack-sm" data-testid="ai-coach-brief">
          <h3>Today&apos;s Brief</h3>
          <p>{brief.message}</p>
          {brief.todaysFocus && (
            <p className="helper">
              Focus: {brief.todaysFocus.concept || brief.todaysFocus.skill} · {brief.todaysFocus.difficulty} · ~{brief.todaysFocus.estimatedMinutes} min
            </p>
          )}
        </div>
      )}

      <div className="card stack-sm" data-testid="ai-coach-goal">
        <h3>Current Goal</h3>
        {context?.activeGoal ? <p><strong>{context.activeGoal.title}</strong></p> : <p className="text-muted">No goal yet — set one and your plan adapts automatically.</p>}
        <div className="button-row">
          <input className="input" value={goalTitle} placeholder="e.g. Earn Hospitality Foundations" onChange={(event) => setGoalTitle(event.target.value)} />
          <button type="button" className="btn btn-primary" disabled={busy || !goalTitle.trim()} onClick={() => void addGoal()}>Set goal</button>
        </div>
      </div>

      {plan.length > 0 && (
        <div className="card stack-sm" data-testid="ai-coach-plan">
          <h3>Learning Plan{plan[0].alignedGoal ? ` · ${plan[0].alignedGoal}` : ''}</h3>
          {plan.slice(0, 1).map((skillPlan) => (
            <div key={skillPlan.skillName} className="stack-sm">
              <strong>{skillPlan.skillName}{skillPlan.certificationTitle ? ` → ${skillPlan.certificationTitle} (${skillPlan.certificationProgress}%)` : ''}</strong>
              {skillPlan.weeks.map((week) => (
                <p key={week.week} className="helper">Week {week.week}: {week.focus.join(', ')} (~{week.estimatedMinutes} min)</p>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="card stack-sm" data-testid="ai-coach-ask">
        <h3>Ask Coach</h3>
        <div className="stack-sm">
          {messages.map((message) => (
            <p key={message.id} className={message.role === 'coach' ? 'helper' : ''}><strong>{message.role === 'coach' ? 'Coach' : 'You'}:</strong> {message.content}</p>
          ))}
        </div>
        <div className="button-row">
          <input className="input" value={draft} placeholder="I don't understand articles…" onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void ask() }} />
          <button type="button" className="btn btn-primary" disabled={busy || !draft.trim()} onClick={() => void ask()}>Ask</button>
        </div>
      </div>

      {workforce.length > 0 && (
        <div className="card stack-sm" data-testid="ai-coach-workforce">
          <h3>Work-Aware Recommendations</h3>
          {workforce.map((rec, index) => (
            <p key={index} className="helper">{rec.reason} {rec.suggestedAction}</p>
          ))}
        </div>
      )}

      {review && (
        <div className="card stack-sm" data-testid="ai-coach-weekly">
          <h3>Weekly Review</h3>
          <p>{review.message}</p>
          <p className="helper">Time studied: {review.timeStudiedLabel} · Strong: {review.strongAreas.join(', ') || '—'}</p>
        </div>
      )}
    </section>
  )
}
